document.addEventListener("DOMContentLoaded", function () {
    initSectionAnimation('.ctm-services-image-with-text-01', '.section-image-with-text');
    initMediaSectionAnimation('.template-page-media .ctm-media-image-with-text-04', '.section-image-with-text-block');
    initMediaSectionAnimation('.template-page-impact-new .ctm-media-image-with-text-04:not(.gsap-effect) .ctm-sticky-service.ctm-sticky-service-desktop', '.section-image-with-text-block');
    initImpactNewInfographicSync();

    /**
     * @param {string} sectionSelector
     * @param {string} itemSelector
     */
    function initSectionAnimation(sectionSelector, itemSelector) {
        const sections = document.querySelectorAll(sectionSelector);

        sections.forEach(section => {
            /** @type {NodeListOf<HTMLElement>} */
            const items = section.querySelectorAll(itemSelector);

            if (!items.length) return;

            const observerOptions = {
                root: null,
                rootMargin: '0px',
                threshold: buildThresholdList()
            };

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const element = /** @type {HTMLElement} */ (entry.target);
                    const ratio = entry.intersectionRatio;

                    if (entry.isIntersecting) {
                        // Progressive opacity (0 -> 1)
                        element.style.opacity = String(ratio);

                        // Optional subtle upward movement
                        const translateY = 30 * (1 - ratio);
                        element.style.transform = `translateY(${translateY}px)`;
                    } else {
                        // Fully reset when out of viewport
                        element.style.opacity = '0';
                        element.style.transform = `translateY(30px)`;
                    }
                });
            }, observerOptions);

            items.forEach(item => {
                // Set initial state
                item.style.opacity = '0';
                item.style.transform = 'translateY(30px)';
                item.style.transition = 'opacity 0.1s linear, transform 0.1s linear';
                observer.observe(item);
            });
        });
    }

    /**
     * Media section: opacity is 1 only inside a viewport active zone.
     * @param {string} sectionSelector
     * @param {string} itemSelector
     */
    function initMediaSectionAnimation(sectionSelector, itemSelector) {
        const sections = document.querySelectorAll(sectionSelector);
        if (!sections.length) return;

        /** @type {Window & { gsap?: any; ScrollTrigger?: any }} */
        const win = window;
        const gsapLib = win.gsap;
        const ScrollTriggerPlugin = win.ScrollTrigger;

        if (!gsapLib || !ScrollTriggerPlugin) return;

        gsapLib.registerPlugin(ScrollTriggerPlugin);

        const root = document.documentElement;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const minWidthQuery = window.matchMedia('(min-width: 750px)');

        /** Desktop / mobile defaults when CSS vars are unset (viewport < 750px uses mobile defaults). */
        const zoneDefaultsDesktop = { top: 20, bottom: 80, feather: 8, dim: 0.15 };
        const zoneDefaultsMobile = { top: 20, bottom: 40, feather: 12, dim: 0.15 };

        /** @type {HTMLElement[]} */
        const allItems = [];
        sections.forEach(section => {
            section.querySelectorAll(itemSelector).forEach(el =>
                allItems.push(/** @type {HTMLElement} */ (el))
            );
        });

        if (!allItems.length) return;

        if (prefersReducedMotion) {
            gsapLib.set(allItems, { opacity: 1, clearProps: 'transform,opacity' });
            return;
        }

        gsapLib.set(allItems, { clearProps: 'transform' });

        function updateActiveZoneOpacity() {
            const computed = getComputedStyle(root);
            const isDesktop = minWidthQuery.matches;
            const d = isDesktop ? zoneDefaultsDesktop : zoneDefaultsMobile;

            const zoneTopPercent = parsePercent(computed.getPropertyValue('--media-active-zone-top'), d.top);
            const zoneBottomPercent = parsePercent(computed.getPropertyValue('--media-active-zone-bottom'), d.bottom);
            const edgeFeatherPercent = parsePercent(computed.getPropertyValue('--media-active-zone-feather'), d.feather);
            const dimParsed = parseFloat(computed.getPropertyValue('--media-dim-opacity'));
            const dimOpacity = Number.isFinite(dimParsed) ? dimParsed : d.dim;

            const viewportHeight = window.innerHeight;
            const zoneTop = viewportHeight * (zoneTopPercent / 100);
            const zoneBottom = viewportHeight * (zoneBottomPercent / 100);
            const edgeFeather = viewportHeight * (edgeFeatherPercent / 100);

            allItems.forEach(item => {
                const rect = item.getBoundingClientRect();
                const centerY = (rect.top + rect.bottom) / 2;

                let opacity = dimOpacity;

                if (centerY >= zoneTop && centerY <= zoneBottom) {
                    opacity = 1;
                } else if (centerY > zoneTop - edgeFeather && centerY < zoneTop) {
                    const t = (centerY - (zoneTop - edgeFeather)) / edgeFeather;
                    opacity = dimOpacity + ((1 - dimOpacity) * t);
                } else if (centerY > zoneBottom && centerY < zoneBottom + edgeFeather) {
                    const t = 1 - ((centerY - zoneBottom) / edgeFeather);
                    opacity = dimOpacity + ((1 - dimOpacity) * t);
                }

                item.style.opacity = String(opacity);
            });
        }

        let refreshTimeout = 0;
        window.addEventListener('resize', () => {
            window.clearTimeout(refreshTimeout);
            refreshTimeout = window.setTimeout(() => {
                updateActiveZoneOpacity();
            }, 250);
        });
        minWidthQuery.addEventListener('change', updateActiveZoneOpacity);
        window.addEventListener('scroll', updateActiveZoneOpacity, { passive: true });
        updateActiveZoneOpacity();
    }

    /**
     * Impact page: sync `.section-image-with-text-block` hover with `.infographic-step` panels.
     * Assigns `data-step` by DOM order when missing. When no block is hovered, the first step is active.
     */
    function initImpactNewInfographicSync() {
        const root = document.querySelector('.template-page-impact-new');
        if (!root) return;

        const section = root.querySelector('.ctm-media-image-with-text-04:not(.gsap-effect)');
        if (!section) return;

        const items = section.querySelectorAll('.section-image-with-text-block');
        const steps = section.querySelectorAll('.infographic-step');

        if (!items.length || !steps.length) return;

        items.forEach((el, i) => {
            const node = /** @type {HTMLElement} */ (el);
            if (!node.dataset.step) node.dataset.step = String(i + 1);
        });
        steps.forEach((el, i) => {
            const node = /** @type {HTMLElement} */ (el);
            if (!node.dataset.step) node.dataset.step = String(i + 1);
        });

        /** @type {HTMLElement[]} */
        const blocks = Array.from(items, el => /** @type {HTMLElement} */ (el));

        /**
         * @param {string} step
         */
        function activateForStep(step) {
            blocks.forEach(el => el.classList.toggle('active', el.dataset.step === step));
            steps.forEach(el => {
                const stepEl = /** @type {HTMLElement} */ (el);
                stepEl.classList.toggle('active', stepEl.dataset.step === step);
            });
        }

        function resetToFirstStep() {
            blocks.forEach(el => el.classList.remove('active'));
            steps.forEach((el, i) => {
                /** @type {HTMLElement} */ (el).classList.toggle('active', i === 0);
            });
        }

        resetToFirstStep();

        blocks.forEach(block => {
            block.addEventListener('mouseenter', () => {
                const step = block.dataset.step;
                if (step === undefined) return;
                activateForStep(step);
            });
            block.addEventListener('mouseleave', () => {
                requestAnimationFrame(() => {
                    if (!blocks.some(b => b.matches(':hover'))) {
                        resetToFirstStep();
                    }
                });
            });
        });

        const wrappers = section.querySelectorAll('.section-image-with-text');
        wrappers.forEach(wrapper => {
            wrapper.addEventListener('mouseleave', (e) => {
                const related = /** @type {MouseEvent} */ (e).relatedTarget;
                if (related instanceof Node && wrapper.contains(related)) return;
                requestAnimationFrame(() => {
                    if (!blocks.some(b => b.matches(':hover'))) {
                        resetToFirstStep();
                    }
                });
            });
        });
    }

    /**
     * @param {string} cssValue
     * @param {number} fallback
     */
    function parsePercent(cssValue, fallback) {
        const parsed = parseFloat(cssValue);
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    // Creates smooth threshold steps (important for progressive animation)
    function buildThresholdList() {
        const thresholds = [];
        const steps = 10; // higher = smoother
        for (let i = 0; i <= steps; i++) {
            thresholds.push(i / steps);
        }
        return thresholds;
    }

});


document.addEventListener('DOMContentLoaded', function () {

  const selectEl = /** @type {HTMLSelectElement | null} */ (document.getElementById('mce-MMERGE16'));
  const formEl = document.getElementById('mc_embed_shell');

  /** @type {Record<string, string>} */
  const mapping = {
    worktogether: 'Brand & Marketing Strategy',
    bookakeynote: 'Keynote Speaking or Event Inquiry',
    mediacollaboration: 'Podcast / Media Request'
  };

  function handleHash() {
    const hash = window.location.hash;

    if (!hash.includes('#mc_embed_shell')) return;

    const valueMatch = hash.match(/value=([^&]+)/);
    if (!valueMatch) return;

    if (!valueMatch[1]) return;
    const key = valueMatch[1].toLowerCase();
    const optionValue = mapping[key];

    if (!optionValue) return;

    if (selectEl) {
      selectEl.value = optionValue;
    }

    if (formEl) {
      formEl.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  // Run on page load
  handleHash();

  // Run when hash changes
  window.addEventListener('hashchange', handleHash);

});

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('a[href^="#"]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      const targetId = this.getAttribute('href');

      // ignore empty hashes like "#"
      if (!targetId || targetId === '#') return;

      const targetEl = document.querySelector(targetId);

      if (targetEl) {
        e.preventDefault();

        targetEl.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });
});